import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const MLM_LOGO = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/5mj39m7a_image.png";

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  {
    label: 'The Movement',
    children: [
      { label: 'The Problem', path: '/problem' },
      { label: 'The Solution', path: '/solution' },
      { label: 'Impact', path: '/impact' },
    ],
  },
  {
    label: 'Get Involved',
    children: [
      { label: 'Corporate CSI', path: '/corporate-csi' },
      { label: 'For Riders', path: '/for-riders' },
      { label: 'Advertising', path: '/advertising' },
    ],
  },
  { label: 'Insights', path: '/insights' },
  { label: 'Contact', path: '/contact' },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="navigation-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-lg py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container-wide">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img
              src={MLM_LOGO}
              alt="#MyLifeMatters"
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <DropdownMenu key={link.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                        isScrolled
                          ? 'text-slate-700 hover:text-primary-mlm hover:bg-purple-50'
                          : 'text-slate-800 hover:text-primary-mlm hover:bg-white/50'
                      }`}
                      data-testid={`nav-dropdown-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="bg-white border border-slate-200 shadow-xl rounded-xl p-1 min-w-[180px]">
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link
                          to={child.path}
                          className={`w-full px-4 py-2 text-sm rounded-lg cursor-pointer ${
                            isActive(child.path)
                              ? 'bg-purple-50 text-primary-mlm font-medium'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={`nav-link-${child.path.slice(1)}`}
                        >
                          {child.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary-mlm text-white'
                      : isScrolled
                      ? 'text-slate-700 hover:text-primary-mlm hover:bg-purple-50'
                      : 'text-slate-800 hover:text-primary-mlm hover:bg-white/50'
                  }`}
                  data-testid={`nav-link-${link.path === '/' ? 'home' : link.path.slice(1)}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              asChild
              className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-6 font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="partner-cta-btn"
            >
              <Link to="/corporate-csi">Partner With Us</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-full hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-xl py-4 px-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label} className="space-y-1">
                    <span className="block px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {link.label}
                    </span>
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`block px-4 py-2 rounded-lg text-sm ${
                          isActive(child.path)
                            ? 'bg-purple-50 text-primary-mlm font-medium'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                        data-testid={`mobile-nav-${child.path.slice(1)}`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-2 rounded-lg text-sm ${
                      isActive(link.path)
                        ? 'bg-purple-50 text-primary-mlm font-medium'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`mobile-nav-${link.path === '/' ? 'home' : link.path.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-4 mt-2 border-t border-slate-100">
                <Button
                  asChild
                  className="w-full bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full font-semibold"
                  data-testid="mobile-partner-cta"
                >
                  <Link to="/corporate-csi">Partner With Us</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
